import { useCallback, useEffect, useRef } from 'react'
import { EDITOR_SESSION_ENDED_EVENT, type EditorSessionEndedDetail } from '@papan/collab/client/session-events'

type FileEventLike = { type: string; fileId: string }

export interface EndedTabRecoveryOptions {
  /** The open tabs, read at the moment of each event (a ref, so the listeners never go stale). */
  tabsRef: { readonly current: ReadonlyArray<{ id: string; fileId: string; deleted?: boolean }> }
  /** Greys / un-greys tabs; un-greying with `remount` mounts a fresh editor (`restoredSeq`). */
  setTabsDeleted: (fileIds: readonly string[], deleted: boolean, opts?: { remount?: boolean }) => void
  /** Mounts a fresh editor in tabs that are not greyed (a refused editor). */
  remountTabs: (fileIds: readonly string[]) => void
  /** The server's answer: is this file back? (`tabFileIsBack` — a pointer only with a live target.) */
  fileIsBack: (fileId: string) => Promise<boolean>
  /** Does a delete of `deletedId` reach the tab showing `fileId` (it, or a folder above it)? Unsure → true. */
  deleteTouches: (fileId: string, deletedId: string) => boolean
  /** Bumped when this workspace's access may have changed — a refused tab asks again. */
  accessRefreshKey?: number
  /**
   * Bumped on an access change that names NO one (#1259: a share link minted, an org share
   * changed or restored — every member gets those): ended tabs ask again, as on
   * `accessRefreshKey` — a tab its room keeps refusing waits out its window (`askAllEnded`).
   */
  endedRecheckKey?: number
  onFileEvent: (cb: (event: FileEventLike) => void) => () => void
  onResync: (cb: (kind: 'file') => void) => () => void
}

/** A "back" answer dropped because a delete reached the tab while it was out is asked again after this — once per
 *  file however many deletes land meanwhile (a tenant deleting in a loop must not turn into a poll at round-trip rate). */
export const REASK_AFTER_DELETE_MS = 1_000

/** A refused tab remounted and refused AGAIN within this long is one its room keeps refusing (the detail says yes,
 *  the room says no) — not a later, genuine loss of access, which is asked like any refusal (#1259 review). Well past
 *  the collab server's own re-checks (a cross-org editor is re-checked on a ~30 s share scan, #1261), so a room that
 *  refuses only on its next re-check is still caught — never a remount loop at the scan's period. */
export const REFUSED_AGAIN_WITHIN_MS = 120_000

/** Monotonic: a wall-clock change must not move the window. */
const now = () => performance.now()

/**
 * #823: a workspace tab whose editor the server ENDED comes back only on the server's word.
 *
 * - Its editor host announces the end naming the TAB (`EDITOR_SESSION_ENDED_EVENT`, `hostKey`); this workspace acts
 *   only for its own tabs. Moved to the bin greys the tab; a refusal (4403 — the server's one answer for "binned" and
 *   "no access", #630) is only remembered.
 * - An ended tab asks the server whether its file is back: at the end itself (a quick restore can land before the
 *   editor's own close), on EVERY restore event — whatever it names (a pointer's restore names its target) — on every
 *   resync (the event may have been lost) and, for a refusal, on every access change.
 * - Only a "back" answer brings it back (un-grey, or a fresh editor for a refused one). An answer read before a later
 *   delete that reaches the tab is dropped and asked again.
 * - A refused tab brought back and refused AGAIN soon after (the detail says yes, the room says no) is asked again
 *   on a restore or a resync, or once its window ends after an access change — never an ask-remount loop. A refusal
 *   long after a working remount is a new loss of access, asked like any other.
 */
export function useEndedTabRecovery(opts: EndedTabRecoveryOptions): { ask: (fileId: string) => void } {
  const optsRef = useRef(opts)
  optsRef.current = opts
  const { onFileEvent, onResync, accessRefreshKey, endedRecheckKey } = opts

  const state = useRef({
    /** tabs whose editor was refused (4403) — ended, not greyed */
    refused: new Set<string>(),
    /** refused files remounted since the last restore / resync / own access change: which TAB, and when — a tab
     *  closed and the file reopened is a new tab, never under the old one's guard */
    remountedFromRefusal: new Map<string, { tabId: string; at: number }>(),
    /** refused again within REFUSED_AGAIN_WITHIN_MS of that remount: its room keeps refusing — a restore or a resync
     *  asks it at once; any access change (the viewer's own included, deliberately — round 9: unrelated grants
     *  remounted it) asks it once when its window ends */
    refusingRoom: new Set<string>(),
    /** the latest probe per file — only its answer counts */
    probeSeq: new Map<string, number>(),
    /** per file: bumped by each delete that reaches it — an answer read before it says nothing about now */
    deleteEpoch: new Map<string, number>(),
    /** TAB ids whose editor's session the server ended (moved to the bin) — only they get a fresh editor on un-grey.
     *  Keyed by tab, not file: a tab closed and the file reopened is a new tab with a live editor. Read synchronously,
     *  never through `tabsRef` — the grey is a queued state update not yet visible there. */
    endedTabs: new Set<string>(),
    /** pending coalesced re-asks after a dropped answer */
    reaskTimers: new Map<string, ReturnType<typeof setTimeout>>(),
    /** a `refusingRoom` tab an access change skipped: asked ONCE when its window ends (#1259 review) */
    guardTimers: new Map<string, ReturnType<typeof setTimeout>>(),
    /** unmounted: an answer landing later schedules nothing */
    disposed: false,
  }).current
  useEffect(() => {
    state.disposed = false
    return () => {
      state.disposed = true
      for (const t of state.reaskTimers.values()) clearTimeout(t)
      state.reaskTimers.clear()
      for (const t of state.guardTimers.values()) clearTimeout(t)
      state.guardTimers.clear()
    }
  }, [state])

  const isOpen = (fileId: string) => optsRef.current.tabsRef.current.some(t => t.fileId === fileId)

  const ask = useCallback((fileId: string) => {
    if (!isOpen(fileId)) { state.refused.delete(fileId); return }
    const seq = (state.probeSeq.get(fileId) ?? 0) + 1
    state.probeSeq.set(fileId, seq)
    const epoch = state.deleteEpoch.get(fileId) ?? 0
    void optsRef.current.fileIsBack(fileId).then(back => {
      if (state.disposed || state.probeSeq.get(fileId) !== seq) return
      // a delete reaching this tab landed while the answer was out: it says nothing — ask again, once, a moment later
      if (epoch !== (state.deleteEpoch.get(fileId) ?? 0)) {
        if (!state.reaskTimers.has(fileId)) {
          state.reaskTimers.set(fileId, setTimeout(() => { state.reaskTimers.delete(fileId); ask(fileId) }, REASK_AFTER_DELETE_MS))
        }
        return
      }
      if (!back) return
      const o = optsRef.current
      if (state.refused.delete(fileId)) {
        // a remount starts the file's guard over: any timer still pending is for state that is gone
        const timer = state.guardTimers.get(fileId)
        if (timer) { clearTimeout(timer); state.guardTimers.delete(fileId) }
        const tabId = o.tabsRef.current.find(t => t.fileId === fileId)?.id
        if (tabId) state.remountedFromRefusal.set(fileId, { tabId, at: now() })
        o.remountTabs([fileId])
      }
      // an ended editor's tab un-greys with a fresh editor even when its grey has not rendered yet: both are queued
      // state updates, applied in order — grey, then un-grey and remount
      const tab = o.tabsRef.current.find(t => t.fileId === fileId)
      const remount = !!tab && state.endedTabs.delete(tab.id)
      if (remount || tab?.deleted) o.setTabsDeleted([fileId], false, { remount })
    })
  }, [state])

  /**
   * Ask every ended tab. A RESTORE or a RESYNC (`full`) asks them all and lifts the refusal guard. An ACCESS change
   * (the viewer's own, or one naming no one — any member's link mint, another org restoring its share) asks every
   * ended tab except one whose room refused it again right after its remount (`refusingRoom`, within
   * REFUSED_AGAIN_WITHIN_MS): that one is asked ONCE when its window ends — so it is never stuck (another org giving
   * the file back reaches it), and a room that keeps refusing costs at most one remount per window, however many
   * changes arrive (#1259 reviews).
   */
  const askAllEnded = useCallback((mode: 'full' | 'access' = 'full') => {
    if (mode === 'full') {
      state.remountedFromRefusal.clear(); state.refusingRoom.clear()
      for (const t of state.guardTimers.values()) clearTimeout(t)
      state.guardTimers.clear()
    }
    const tabs = optsRef.current.tabsRef.current
    const open = (id: string) => tabs.some(t => t.fileId === id)
    for (const id of state.refused) if (!open(id)) state.refused.delete(id)
    for (const id of state.refusingRoom) if (!open(id)) state.refusingRoom.delete(id)
    for (const id of state.remountedFromRefusal.keys()) if (!open(id)) state.remountedFromRefusal.delete(id)
    // a closed tab's record goes with it (the file reopened is a new tab)
    for (const id of state.endedTabs) if (!tabs.some(t => t.id === id)) state.endedTabs.delete(id)
    const ended = new Set<string>()
    for (const id of state.refused) {
      const remounted = state.remountedFromRefusal.get(id)
      const waitMs = state.refusingRoom.has(id) && remounted ? REFUSED_AGAIN_WITHIN_MS - (now() - remounted.at) : 0
      if (waitMs <= 0) { ended.add(id); continue }
      if (!state.guardTimers.has(id)) {
        const guardedTab = remounted!.tabId
        state.guardTimers.set(id, setTimeout(() => {
          state.guardTimers.delete(id)
          if (state.disposed || !state.refused.has(id)) return
          // only for the tab it guarded: the file closed and reopened in a new (working) tab is not that tab
          const tabs = optsRef.current.tabsRef.current
          if (!tabs.some(t => t.id === guardedTab && t.fileId === id)) {
            // the guarded tab's REFUSAL records go — all of them are its: a NEW tab of the file refused on
            // its own (`onEnded`) or remounted (`ask`) cancels this timer, so a timer that fires never holds
            // another tab's refusal. A new tab's grey (`endedTabs`, keyed by tab) is not touched: it is asked
            // on its own triggers (a restore, a resync, an access change), as any greyed tab
            state.refused.delete(id); state.refusingRoom.delete(id); state.remountedFromRefusal.delete(id)
            return
          }
          state.refusingRoom.delete(id)
          ask(id)
        }, waitMs))
      }
    }
    for (const t of tabs) if (t.deleted || state.endedTabs.has(t.id)) ended.add(t.fileId)
    for (const id of ended) ask(id)
  }, [state, ask])

  useEffect(() => {
    const onEnded = (e: Event) => {
      const d = (e as CustomEvent<EditorSessionEndedDetail>).detail
      const tab = d && optsRef.current.tabsRef.current.find(t => t.id === d.hostKey)
      if (!tab) return
      if (d.reason === 'deleted') {
        state.endedTabs.add(tab.id)
        optsRef.current.setTabsDeleted([tab.fileId], true)
        ask(tab.fileId)
        return
      }
      state.refused.add(tab.fileId)
      const remounted = state.remountedFromRefusal.get(tab.fileId)
      if (remounted && remounted.tabId === tab.id && now() - remounted.at < REFUSED_AGAIN_WITHIN_MS) {
        state.refusingRoom.add(tab.fileId) // refused again right after its remount: no ask-remount loop
        return
      }
      state.remountedFromRefusal.delete(tab.fileId)
      state.refusingRoom.delete(tab.fileId)
      // a guard timer armed for an EARLIER tab of this file is that tab's — never this one's
      const timer = state.guardTimers.get(tab.fileId)
      if (timer) { clearTimeout(timer); state.guardTimers.delete(tab.fileId) }
      ask(tab.fileId)
    }
    window.addEventListener(EDITOR_SESSION_ENDED_EVENT, onEnded)
    return () => window.removeEventListener(EDITOR_SESSION_ENDED_EVENT, onEnded)
  }, [state, ask])

  useEffect(() => onFileEvent(event => {
    if (event.type === 'file:deleted') {
      for (const t of optsRef.current.tabsRef.current) {
        if (optsRef.current.deleteTouches(t.fileId, event.fileId)) {
          state.deleteEpoch.set(t.fileId, (state.deleteEpoch.get(t.fileId) ?? 0) + 1)
        }
      }
    } else if (event.type === 'file:restored') {
      askAllEnded()
    }
  }), [onFileEvent, state, askAllEnded])

  useEffect(() => onResync(kind => { if (kind === 'file') askAllEnded() }), [onResync, askAllEnded])

  // an access change (granted back?) — a refused tab asks again; the first render is not a change
  const lastAccessKey = useRef(accessRefreshKey)
  useEffect(() => {
    if (lastAccessKey.current === accessRefreshKey) return
    lastAccessKey.current = accessRefreshKey
    askAllEnded('access')
  }, [accessRefreshKey, askAllEnded])

  const lastRecheckKey = useRef(endedRecheckKey)
  useEffect(() => {
    if (lastRecheckKey.current === endedRecheckKey) return
    lastRecheckKey.current = endedRecheckKey
    askAllEnded('access')
  }, [endedRecheckKey, askAllEnded])

  return { ask }
}
